import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSmartLinkContext } from '@atlaskit/link-provider/context';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import type { ConnectionGroup } from '../ConnectionsByLineMenu.tsx';
import { collectSmartLinks } from '../utils/collectSmartLinks.ts';
import {
	getSmartLinkProductGroupLabel,
	groupSmartLinksByProduct,
} from '../utils/groupSmartLinksByProduct.ts';
import { jsonLdToEnhancedSmartLink } from '../utils/jsonLdToEnhancedSmartLink.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import { usePageAdf } from './usePageAdf.ts';

export type UseSmartLinksResult = {
	groups: ConnectionGroup[];
	links: EnhancedSmartLink[];
	loading: boolean;
	error: Error | null;
	refreshLinkUrl: (item: EnhancedSmartLink) => Promise<EnhancedSmartLink | undefined>;
};

const reportSmartLinkError = (err: unknown) => {
	const errorObj = err instanceof Error ? err : new Error(String(err));
	getMonitoringClient().submitError(errorObj, {
		attribution: Attribution.INTEGRATIONS,
	});
	return errorObj;
};

type SmartLinkResponse = Parameters<typeof jsonLdToEnhancedSmartLink>[2];

const mergeRefreshedSmartLink = (
	link: EnhancedSmartLink,
	response: SmartLinkResponse,
): EnhancedSmartLink => {
	const refreshedLink = jsonLdToEnhancedSmartLink(link.url, link.occurrenceIndex, response);
	return {
		...refreshedLink,
		iconUrl: refreshedLink.iconUrl ?? link.iconUrl,
		product: link.product ?? refreshedLink.product,
		subproduct: link.subproduct ?? refreshedLink.subproduct,
	};
};

/**
 * Collects all smart links from the current page ADF, enriches them with
 * metadata from the SmartCard store (cache) and ORS (network fallback), then
 * groups them by product for consumption by ConnectionsByLineMenu.
 */
export function useSmartLinks(): UseSmartLinksResult {
	const { adf } = usePageAdf();
	const { connections, store } = useSmartLinkContext();

	const [links, setLinks] = useState<EnhancedSmartLink[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const linksRef = useRef<EnhancedSmartLink[]>([]);

	// The SmartCard provider rebuilds its context value with `lodash.merge({}, ...)`, which mints a
	// new `store` object on every recompute even though the underlying Redux store is unchanged. We
	// read it through a ref and keep it out of the collection effect's deps so an ancestor re-render
	// (e.g. on scroll) does not re-fire the effect, flash the skeleton, and re-collect the links.
	const storeRef = useRef(store);
	useEffect(() => {
		storeRef.current = store;
	}, [store]);

	useEffect(() => {
		let cancelled = false;

		setLoading(true);
		setError(null);

		collectSmartLinks(adf, storeRef.current, connections.client)
			.then((result) => {
				if (!cancelled) {
					linksRef.current = result;
					setLinks(result);
					setLoading(false);
				}
			})
			.catch((err: unknown) => {
				if (cancelled) return;
				const errorObj = reportSmartLinkError(err);
				setError(errorObj);
				setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [adf, connections.client]);

	const refreshLinkUrl = useCallback(
		async (item: EnhancedSmartLink): Promise<EnhancedSmartLink | undefined> => {
			const productLabel = getSmartLinkProductGroupLabel(item);
			const productLinks: EnhancedSmartLink[] = [];
			for (const link of linksRef.current) {
				const isSameProduct = getSmartLinkProductGroupLabel(link) === productLabel;
				if (isSameProduct) {
					productLinks.push(link);
				}
			}

			const urlsToRefresh: string[] = [];
			const seenUrls = new Set<string>();
			for (const link of productLinks) {
				if (!seenUrls.has(link.url)) {
					seenUrls.add(link.url);
					urlsToRefresh.push(link.url);
				}
			}

			const refreshedDataByUrl = new Map<string, SmartLinkResponse>();
			await Promise.all(
				urlsToRefresh.map(async (url) => {
					try {
						const response = await connections.client.fetchData(url, true);
						refreshedDataByUrl.set(url, response);
					} catch (err: unknown) {
						reportSmartLinkError(err);
					}
				}),
			);

			if (refreshedDataByUrl.size === 0) {
				return undefined;
			}

			let refreshedMatchingItem: EnhancedSmartLink | undefined;
			const refreshedLinks = linksRef.current.map((link) => {
				if (getSmartLinkProductGroupLabel(link) !== productLabel) {
					return link;
				}
				const response = refreshedDataByUrl.get(link.url);
				if (!response) {
					return link;
				}
				const refreshedLink = mergeRefreshedSmartLink(link, response);
				if (link.url === item.url && link.occurrenceIndex === item.occurrenceIndex) {
					refreshedMatchingItem = refreshedLink;
				}
				return refreshedLink;
			});

			linksRef.current = refreshedLinks;
			setLinks(refreshedLinks);
			return refreshedMatchingItem;
		},
		[connections.client],
	);

	const groups = useMemo(() => groupSmartLinksByProduct(links), [links]);

	return { groups, links, loading, error, refreshLinkUrl };
}
